/**
 * Thin, dependency-light PostHog fan-out.
 *
 * PRIVACY GUARDRAIL (critical — this is client financial data): PostHog only
 * ever receives event names + a strict allowlist of non-sensitive, scalar
 * metadata + the `ai_trace_id`. Financial values, free-text notes, bug-report
 * messages, raw prompts, and model responses are NEVER sent here — those stay
 * first-party (Postgres + BigQuery). The fan-out is env-gated on the project
 * key and is a complete no-op when the key is absent.
 */

import type { AnalyticsProps, PostHogConfig } from './types'

const DEFAULT_HOST = 'https://us.i.posthog.com'

/**
 * Top-level metadata keys that are safe to forward to PostHog. Anything not on
 * this list is dropped. Values must additionally be scalar (string/number/
 * boolean) — nested objects/arrays are never forwarded, since they are the most
 * likely place for financial values or model text to hide.
 */
const SAFE_KEYS: ReadonlySet<string> = new Set([
  'surface',
  'ai_trace_id',
  'verdict',
  'source',
  'kind',
  'label',
  'tab',
  'mode',
  'section',
  'provider',
  'model_id',
  'card_id',
  'journey',
  'analysis_mode',
  'output_kind',
  // Guided tours. All scalar and non-financial; the lead email deliberately
  // stays first-party in `demo_leads` and is never fanned out here.
  'tour_slug',
  'tour_version',
  'tour_source',
  'context',
  'embed',
  'step_id',
  'step_index',
  'step_count',
  'chapter_id',
  'dwell_ms',
  'industry',
  'exit_reason',
  'anchor_id',
  'anchor_kind',
  'cta',
  'method',
  'variant',
])

export function sanitizeForPostHog(props: AnalyticsProps): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(props || {})) {
    if (!SAFE_KEYS.has(key)) continue
    if (value === null || typeof value === 'undefined') continue
    const t = typeof value
    if (t === 'string' || t === 'number' || t === 'boolean') {
      out[key] = value as string | number | boolean
    }
  }
  return out
}

export class PostHogFanout {
  private readonly key: string
  private readonly host: string
  private distinctId: string

  private constructor(key: string, host: string) {
    this.key = key
    this.host = host.replace(/\/+$/, '')
    this.distinctId = 'anon'
  }

  /** Returns null (no-op) when no key is configured. */
  static fromConfig(config?: PostHogConfig | null): PostHogFanout | null {
    const key = String(config?.key ?? '').trim()
    if (!key) return null
    const host = String(config?.host ?? '').trim() || DEFAULT_HOST
    return new PostHogFanout(key, host)
  }

  setDistinctId(id: string | null | undefined): void {
    const v = String(id ?? '').trim()
    if (v) this.distinctId = v
  }

  /** Fire-and-forget capture. Swallows all errors; analytics must never throw. */
  capture(event: string, props: AnalyticsProps): void {
    const safe = sanitizeForPostHog(props)
    const body = {
      api_key: this.key,
      event,
      distinct_id: this.distinctId,
      properties: { ...safe, $lib: 'fusegrid-analytics' },
      timestamp: new Date().toISOString(),
    }
    try {
      void fetch(`${this.host}/i/v0/e/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {})
    } catch {
      /* never throw from analytics */
    }
  }
}
